import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import isString from 'lodash/isString'
import map from 'lodash/map'
import { useRouter } from 'next/router'
import React, { KeyboardEvent, MutableRefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import SvgAngleRight from '@Assets/SVG/AngleRight'
import SvgClock from '@Assets/SVG/Clock'
import SvgClose from '@Assets/SVG/Close'
import SvgSearch2 from '@Assets/SVG/Search2'
import { ImageV4 } from '@Components/Image'
import { Link } from '@Components/Link'
import { Button } from '@CoreComponents/Buttons/Button'
import { addSearchFromFreeText, usePreviousSearches } from '@Domains/Navigation/Features/Search/PreviousSearches'
import { useSearchIntent } from '@Domains/Navigation/Features/Search/SearchIntent'
import { search, SearchResults } from '@Globals/Algolia/Search'
import { generateEventID } from '@Globals/Google/DataLayer'
import { gtagEvent } from '@Globals/Google/GTag'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'

import styles from './Search.module.scss'

const arrowNavigate = (
  event: KeyboardEvent,
  index: number,
  columns: number,
  elements: HTMLElement[],
  inputRef: MutableRefObject<HTMLInputElement | null>,
) => {
  if (document.activeElement?.localName === 'input') {
    return index
  }
  const focusInput = () => {
    inputRef.current?.focus()
    setTimeout(
      () => inputRef.current?.setSelectionRange(inputRef.current?.value.length, inputRef.current?.value.length),
      0,
    )
  }
  const e = event || window.event
  let i: number = index
  const focusElements = elements
  if (e.key === 'ArrowUp') {
    i = Math.max(i - 1, -1)
    if (i < 0) {
      focusInput()
    } else {
      focusElements[i]?.focus()
    }
  } else if (e.key === 'ArrowDown') {
    i = Math.min(i + 1, focusElements.length)
    focusElements[i]?.focus()
  } else if (e.key === 'ArrowLeft') {
    if (i === 1) {
      focusInput()
    } else {
      i = 0
      focusElements[0]?.focus()
    }
  } else if (e.key === 'ArrowRight') {
    i = columns
    focusElements[columns]?.focus()
  }
  return i
}

export const Search = () => {
  const { t } = useTranslation()
  const previousWrapperRef = useRef<HTMLDivElement | null>(null)
  const suggestionsWrapperRef = useRef<HTMLDivElement | null>(null)
  const productsWrapperRef = useRef<HTMLDivElement | null>(null)
  const { storeURL } = useContext(MetadataContext)
  const { locale = 'en-GB' } = useRouter() || {}
  const [searchWord, setSearchWord] = useState('')
  const [searchResult, setSearchResult] = useState<SearchResults | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const resolveIntentFilters = useSearchIntent(locale)
  const [previousSearches, removePreviousSearchByIndex] = usePreviousSearches()
  const searchAllURL = useMemo(() => storeURL('search/results', new URLSearchParams({ q: searchWord })), [searchWord, storeURL])

  useEffect(() => {
    const onKeyDown = (e) => {
      const refs1 = ([
        inputRef,
        ...previousWrapperRef.current?.children as unknown as HTMLElement[],
        ...suggestionsWrapperRef.current?.children as unknown as HTMLElement[],
      ] || []) as unknown as HTMLElement[]
      const refs2 = productsWrapperRef.current?.children as unknown as HTMLElement[]
      const refs = [...refs1, ...refs2] as unknown as HTMLElement[] || []
      const focusElements = refs.filter(element => element.localName === 'a' || element.localName === 'input')
      const splitToProducts = refs1.filter(element => element.localName === 'a' || element.localName === 'input').length
      arrowNavigate(e, focusElements.findIndex(a => a === document.activeElement), splitToProducts, focusElements, inputRef)
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', onKeyDown)
    }
    if (inputRef.current) {
      inputRef.current.focus()
      ampli.searchStart()
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('keydown', onKeyDown)
      }
    }
  }, [])
  const onMouseEnter = useCallback((e) => {
    if (typeof document !== 'undefined' && document.activeElement?.localName === 'input') {
      return
    }
    e.currentTarget.focus()
  }, [])

  const onSearch = useCallback((word: string) => {
    setSearchWord(word)
    if (word.length >= 1) {
      // Boosts only reorder what the index already returns, so an empty array — no
      // vocabulary matched, or the dictionary hasn't loaded yet — still runs
      // the same search on the same index, just unboosted.
      search(locale, word, resolveIntentFilters(word)).then(setSearchResult).catch(() => {})
    } else {
      setSearchResult(null)
    }
  }, [locale, resolveIntentFilters])

  return (
    <div className={styles.search}>
      <section>
        <div className={styles.searchWrapper}>
          <SvgSearch2 />
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (typeof document !== 'undefined' && document.activeElement === inputRef.current && searchWord.length >= 1 && searchResult) {
                addSearchFromFreeText(searchWord, searchAllURL)
                gtagEvent('search', {
                  event_id: generateEventID(),
                  search_term: searchWord,
                })
                ampli.navLinkClick({
                  dest_page_type: 'searchresults',
                  dest_page_type_id: '',
                  search_query: searchWord,
                  source_id: 'site_search_results',
                })
                window.location.href = searchAllURL
              }
            }}
            style={{ width: '100%' }}
          >
            <input
              enterKeyHint="search"
              name="search"
              onChange={e => onSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.currentTarget.blur()
                }
              }}
              onPaste={(e) => {
                e.preventDefault()
                const paste = e.clipboardData.getData('Text')
                if (isString(paste)) {
                  onSearch(paste)
                }
              }}
              placeholder={t('header:HEADER_MOBILE_SEARCH_LABEL')}
              ref={inputRef}
              type="search"
              value={searchWord}
            />
          </form>
          {!!searchWord.length && (
            <button
              onClick={() => {
                setSearchWord('')
                setSearchResult(null)
              }}
              type="button"
            >
              {' '}
              <SvgClose />
            </button>
          )}
        </div>
        <div
          className={clsx(
            styles.previousSearches,
            (!isEmpty(searchResult?.categories)
              || !isEmpty(searchResult?.products)
              || isEmpty(previousSearches)) && styles.hide,
          )}
          ref={previousWrapperRef}
        >
          <p className={styles.header}>{t('header:PREVIOUS_SEARCHES')}</p>
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          {!(!isEmpty(searchResult?.categories)
            || !isEmpty(searchResult?.products)
            || isEmpty(previousSearches)) && map(previousSearches, (previousSearch, previousSearchIndex) => (
              <Link
                key={`previously_${previousSearch.title}`}
                analytics={{
                  dest_page_type: 'searchresults',
                  dest_page_type_id: '',
                  search_query: searchWord,
                  source_id: 'site_search_results',
                }}
                className={styles.previousSearch}
                href={storeURL(previousSearch.slug)}
                onMouseEnter={onMouseEnter}
              >
                <SvgClock />
                <span>{previousSearch.title}</span>
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    removePreviousSearchByIndex(previousSearchIndex)
                  }}
                  theme="label"
                >
                  <SvgClose />
                </Button>
              </Link>
          ))}
        </div>
        <div className={clsx(styles.suggestions, (!searchResult?.categories || isEmpty(searchResult.categories)) && styles.hide)} ref={suggestionsWrapperRef}>
          {/* eslint-disable-next-line no-underscore-dangle */}
          {map(searchResult?.categories, category => category?._highlightResult?.title?.value && (
            <Link
              key={`suggestion_${category.objectID}`}
              analytics={{
                dest_page_type: 'category',
                search_query: searchWord,
                source_id: 'site_search_results',
                dest_page_type_id: category.objectID,
              }}
              className={styles.suggestion}
              href={storeURL(category.slug)}
              onMouseEnter={onMouseEnter}
            >
              <span>
                {category.parentTitle && <p>{category.parentTitle}</p>}
                {category.parentTitle && <SvgAngleRight />}
                {/* eslint-disable-next-line no-underscore-dangle */}
                <p dangerouslySetInnerHTML={{ __html: category._highlightResult.title.value }} />
              </span>
              <SvgAngleRight className={styles.navigationArrow} />
            </Link>
          ))}
        </div>
        <div className={clsx(styles.sideWing, !searchResult && styles.hide)}>
          <div
            className={clsx(styles.products, (!searchResult?.products || isEmpty(searchResult.products)) && styles.hide)}
            ref={productsWrapperRef}
          >
            <p className={styles.header}>{t('header:PRODUCTS')}</p>
            {/* eslint-disable-next-line no-underscore-dangle */}
            {map(searchResult?.products, product => product?._highlightResult?.title?.value && (
              <Link
                key={`products_${product.objectID}`}
                analytics={{
                  dest_page_type: 'product',
                  source_id: 'site_search_results',
                  dest_page_type_id: product.objectID,
                }}
                className={styles.product}
                draggable={false}
                href={storeURL(product.slug)}
                onMouseEnter={onMouseEnter}
              >
                <ImageV4 draggable={false} src={product.primaryAsset.url} width={60} />
                {/* eslint-disable-next-line no-underscore-dangle */}
                <p dangerouslySetInnerHTML={{ __html: product._highlightResult.title.value }} />
                <span>
                  <p className={styles.discount}>{product.displayPrice.discount?.format}</p>
                  <p className={clsx(styles.price, product.displayPrice.discount && styles.strikeThough)}>{product.displayPrice.format}</p>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <img
        alt="Trendhim"
        className={clsx(
          styles.trendhimLogo,
          (isEmpty(searchResult?.categories) && isEmpty(searchResult?.products) && isEmpty(previousSearches))
          && styles.showMobile,
          (
            (searchWord.length >= 1 && isEmpty(searchResult?.categories) && isEmpty(previousSearches))
            || (searchWord.length < 1 && isEmpty(previousSearches) && isEmpty(searchResult?.categories))
          ) && styles.showDesktop,
        )}
        src="https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/blt59296545a01439bf/trendhim-logo-with-text-v2.svg"
      />
      {searchWord.length >= 1 && searchResult && (
        <footer>
          <Button
            analytics={{
              dest_page_type: 'searchresults',
              dest_page_type_id: '',
              search_query: searchWord,
              source_id: 'site_search_results',
            }}
            href={searchAllURL}
            innerSpacing="medium"
            onClick={() => { addSearchFromFreeText(searchWord, searchAllURL) }}
            theme="black"
          >
            {t('header:SHOW_RESULTS')}
          </Button>
        </footer>
      )}
    </div>
  )
}

